import { useRef, useState, useEffect, useCallback } from "react";

/**
 * Tracks horizontal scroll state of a container and returns a className
 * that toggles fade-edges-x[-left|-right|-both] based on whether there
 * is more content to scroll to in each direction.
 *
 * Usage:
 *   const { ref, edgeClass } = useFadeEdges();
 *   <div ref={ref} className={`flex overflow-x-auto scrollbar-hide ${edgeClass}`}>
 */
export default function useFadeEdges() {
  const ref = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const recompute = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setEdges((cur) => (cur.left === left && cur.right === right ? cur : { left, right }));
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    recompute();
    el.addEventListener("scroll", recompute, { passive: true });
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", recompute);
      ro.disconnect();
    };
  }, [recompute]);

  let edgeClass = "";
  if (edges.left && edges.right) edgeClass = "fade-edges-x-both";
  else if (edges.left) edgeClass = "fade-edges-x-left";
  else if (edges.right) edgeClass = "fade-edges-x-right";

  return { ref, edgeClass, recompute };
}
